import { Component, OnInit } from '@angular/core'
import { Firestore, collection, doc, getDoc, setDoc, Timestamp } from '@angular/fire/firestore'
import { Functions, httpsCallable } from '@angular/fire/functions'
import { FirestoreStructure } from '@parkupp/core'
import Swal from 'sweetalert2'

interface Migration {
    id: string
    name: string
    description: string
    category: string
    functionName: string
    isHttpCallable: boolean // true = callable function, false = HTTP request
    dangerLevel: 'safe' | 'warning' | 'danger'
    runCount?: number
    lastRunAt?: Date
    lastRunBy?: string
    lastRunResult?: string
    isRunning?: boolean
}

interface MigrationStatus {
    runCount: number
    lastRunAt: Timestamp
    lastRunBy: string
    lastRunResult: string
}

interface ParkerMigrationStatus {
    totalClients: number
    clientsWithFirstName: number
    clientsWithOccupants: number
    clientsWithDefaultParker: number
    clientsNeedingMigration: number
    totalOccupants: number
    totalVehiclesMigrated: number
    migrationComplete: boolean
}

@Component({
    selector: 'app-admin-migrations',
    templateUrl: './admin-migrations.component.html',
    styleUrls: ['./admin-migrations.component.scss'],
})
export class AdminMigrationsComponent implements OnInit {
    parkerMigrationStatus: ParkerMigrationStatus | null = null
    parkerMigrationStatusLoading = false
    parkerMigrationStatusError: string | null = null

    migrations: Migration[] = [
        {
            id: 'migrateToEnterprise',
            name: 'Migrate to Enterprise',
            description: 'Assigns all existing merchants, parkings, subscriptions, invoices, payments, payouts, bookings, clients, and leads to the default "parkupp" enterprise. Run once when deploying enterprise feature.',
            category: 'Enterprise',
            functionName: 'migrations-migrateToEnterprise', // Nested under migrations object
            isHttpCallable: true,
            dangerLevel: 'warning',
        },
        {
            id: 'backfillAdminClientEnterpriseKey',
            name: 'Backfill AdminClient Enterprise Key',
            description: 'Sets denormEnterpriseKey to "parkupp" for all AdminClient records that don\'t have an enterprise key set. Safe to run multiple times.',
            category: 'Enterprise',
            functionName: 'migrations-backfillAdminClientEnterpriseKey', // Nested under migrations object
            isHttpCallable: true,
            dangerLevel: 'safe',
        },
        {
            id: 'backfillInvoiceEnterpriseKey',
            name: 'Backfill Invoice Enterprise Key',
            description: 'Sets denormEnterpriseKey for all Invoice records that don\'t have an enterprise key set. Tries to get the key from the linked subscription first, defaults to "parkupp". Safe to run multiple times.',
            category: 'Enterprise',
            functionName: 'migrations-backfillInvoiceEnterpriseKey', // Nested under migrations object
            isHttpCallable: true,
            dangerLevel: 'safe',
        },
        {
            id: 'syncAdminClientsAuthData',
            name: 'Sync Auth Data to AdminClients',
            description: 'Updates email addresses and authentication data from Firebase Auth to the Admin Clients table. One-time migration - new auth changes are now synced automatically.',
            category: 'Data Sync',
            functionName: 'syncAdminClientsAuthData', // Exported directly
            isHttpCallable: true,
            dangerLevel: 'safe',
        },
        {
            id: 'syncAdminClientsSubscriptionData',
            name: 'Sync Subscription Data to AdminClients',
            description: 'Updates subscription information (hasActiveSubscription, etc.) for all clients in the Admin Clients table.',
            category: 'Data Sync',
            functionName: 'syncAdminClientsSubscriptionData', // Exported directly
            isHttpCallable: true,
            dangerLevel: 'safe',
        },
        {
            id: 'migrateReviewRequested',
            name: 'Mark Old Subscriptions as Review Requested',
            description: 'Marks all subscriptions older than 10 days as "review requested" to prevent sending review requests to old clients. Run before sending review requests.',
            category: 'Review Requests',
            functionName: 'migrateReviewRequested', // Exported directly
            isHttpCallable: true,
            dangerLevel: 'safe',
        },
        {
            id: 'migrateResetIncorrectlyConvertedLeads',
            name: 'Reset Incorrectly Converted Leads',
            description: 'Finds CRM leads marked as "Converted" who never had an active/completed subscription and resets them to "New" status.',
            category: 'CRM Cleanup',
            functionName: 'migrateResetIncorrectlyConvertedLeads',
            isHttpCallable: true,
            dangerLevel: 'warning',
        },
        {
            id: 'migrateOccupantsToParkers',
            name: 'Migrate Occupants to Parkers',
            description: 'Creates a default Parker per client, copies vehicles from clients/{uid}/occupants to clients/{uid}/parkers/{id}/vehicles, moves admytTenantUserId to the Parker, and sets parkerRefs on subscriptions. Safe to re-run (idempotent).',
            category: 'Parker Architecture',
            functionName: 'httpMigrateOccupantsToParkers',
            isHttpCallable: true,
            dangerLevel: 'warning',
        },
        {
            id: 'reconcileAdmytAccess',
            name: 'Reconcile Admyt Access Flags',
            description: 'Ensures hasAdmytAccess is set to true for all clients with active subscriptions on Admyt-linked parkings. Safety net for the real-time listener.',
            category: 'Admyt',
            functionName: 'testReconcileAdmytAccess',
            isHttpCallable: true,
            dangerLevel: 'safe',
        },
    ]

    loading = true
    categories: string[] = []

    constructor(private firestore: Firestore, private functions: Functions) {}

    async ngOnInit(): Promise<void> {
        // Extract unique categories
        this.categories = [...new Set(this.migrations.map((m) => m.category))]

        // Load migration statuses from Firestore
        await this.loadMigrationStatuses()
        this.loading = false
    }

    async loadMigrationStatuses(): Promise<void> {
        for (const migration of this.migrations) {
            try {
                const migrationDoc = await getDoc(doc(this.firestore, FirestoreStructure.MIGRATIONS, migration.id))
                if (migrationDoc.exists()) {
                    const data = migrationDoc.data() as MigrationStatus
                    migration.runCount = data.runCount || 0
                    migration.lastRunAt = data.lastRunAt?.toDate()
                    migration.lastRunBy = data.lastRunBy
                    migration.lastRunResult = data.lastRunResult
                }
            } catch (error) {
                console.error(`Error loading status for migration ${migration.id}:`, error)
            }
        }
    }

    getMigrationsByCategory(category: string): Migration[] {
        return this.migrations.filter((m) => m.category === category)
    }

    getDangerLevelClass(level: string): string {
        switch (level) {
            case 'safe':
                return 'badge bg-success'
            case 'warning':
                return 'badge bg-warning text-dark'
            case 'danger':
                return 'badge bg-danger'
            default:
                return 'badge bg-secondary'
        }
    }

    getButtonClass(level: string): string {
        switch (level) {
            case 'safe':
                return 'btn btn-outline-success btn-sm'
            case 'warning':
                return 'btn btn-outline-warning btn-sm'
            case 'danger':
                return 'btn btn-outline-danger btn-sm'
            default:
                return 'btn btn-outline-secondary btn-sm'
        }
    }

    async runMigration(migration: Migration): Promise<void> {
        if (migration.isRunning) return

        if (migration.id === 'migrateOccupantsToParkers') {
            return this.runParkerMigrationWithResume()
        }

        const confirmResult = await Swal.fire({
            title: `Run ${migration.name}?`,
            html: `
                <p>${migration.description}</p>
                ${migration.runCount ? `<p class="text-muted"><small>This migration has been run ${migration.runCount} time(s) before.</small></p>` : ''}
            `,
            icon: migration.dangerLevel === 'danger' ? 'warning' : 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, run migration',
            cancelButtonText: 'Cancel',
            confirmButtonColor: migration.dangerLevel === 'danger' ? '#dc3545' : migration.dangerLevel === 'warning' ? '#ffc107' : '#28a745',
        })

        if (!confirmResult.isConfirmed) return

        migration.isRunning = true

        try {
            let result: any

            if (migration.isHttpCallable) {
                const callable = httpsCallable(this.functions, migration.functionName, {
                    timeout: 600000,
                })
                result = await callable({})
            } else {
                const callable = httpsCallable(this.functions, migration.functionName, {
                    timeout: 600000,
                })
                result = await callable({})
            }

            // Update migration status in Firestore
            await this.updateMigrationStatus(migration, 'success', result)

            await Swal.fire({
                title: 'Migration Complete!',
                html: `<pre class="text-start small">${JSON.stringify(result.data || result, null, 2)}</pre>`,
                icon: 'success',
                confirmButtonText: 'OK',
            })

            // Reload statuses
            await this.loadMigrationStatuses()
        } catch (error: any) {
            console.error(`Error running migration ${migration.id}:`, error)

            await this.updateMigrationStatus(migration, 'error', error.message)

            await Swal.fire({
                title: 'Migration Failed',
                text: error.message || 'An error occurred while running the migration.',
                icon: 'error',
                confirmButtonText: 'OK',
            })

            // Reload statuses
            await this.loadMigrationStatuses()
        } finally {
            migration.isRunning = false
        }
    }

    private async updateMigrationStatus(migration: Migration, status: string, result: any): Promise<void> {
        try {
            const migrationRef = doc(this.firestore, FirestoreStructure.MIGRATIONS, migration.id)
            const existingDoc = await getDoc(migrationRef)
            const existingData = existingDoc.exists() ? (existingDoc.data() as MigrationStatus) : null

            await setDoc(migrationRef, {
                runCount: (existingData?.runCount || 0) + 1,
                lastRunAt: Timestamp.now(),
                lastRunBy: 'admin', // TODO: Get actual admin user
                lastRunResult: status === 'success' ? JSON.stringify(result.data || result) : `Error: ${result}`,
                migrationName: migration.name,
                updatedAt: Timestamp.now(),
            })
        } catch (error) {
            console.error('Error updating migration status:', error)
        }
    }

    formatDate(date: Date | undefined): string {
        if (!date) return 'Never'
        return date.toLocaleString()
    }

    async checkParkerMigrationStatus(): Promise<void> {
        this.parkerMigrationStatusLoading = true
        this.parkerMigrationStatusError = null
        try {
            const callable = httpsCallable(this.functions, 'httpParkerMigrationStatus', { timeout: 600000 })
            const result = await callable({})
            this.parkerMigrationStatus = result.data as ParkerMigrationStatus
        } catch (error: any) {
            this.parkerMigrationStatusError = error.message || 'Failed to fetch status'
            console.error('Error checking parker migration status:', error)
        } finally {
            this.parkerMigrationStatusLoading = false
        }
    }

    async runParkerMigrationWithResume(): Promise<void> {
        const migration = this.migrations.find((m) => m.id === 'migrateOccupantsToParkers')
        if (!migration || migration.isRunning) return

        const resumeResult = await Swal.fire({
            title: 'Run Parker Migration',
            input: 'text',
            inputLabel: 'Leave blank to start from the beginning, or paste the last processed Client ID to resume from where it left off.',
            inputPlaceholder: 'e.g. XJxg3B0VCaYhwhKBz5k49eBoEVj1',
            showCancelButton: true,
            confirmButtonText: 'Run Migration',
        })
        if (resumeResult.isDismissed) return

        migration.isRunning = true
        try {
            const callable = httpsCallable(this.functions, 'httpMigrateOccupantsToParkers', { timeout: 600000 })
            const result = await callable({ startAfterClientId: resumeResult.value || null })

            await this.updateMigrationStatus(migration, 'success', result)

            await Swal.fire({
                title: 'Migration Run Complete',
                html: `<pre class="text-start small">${JSON.stringify(result.data || result, null, 2)}</pre>`,
                icon: 'success',
                confirmButtonText: 'OK',
            })

            await this.loadMigrationStatuses()
        } catch (error: any) {
            console.error('Error running parker migration:', error)
            await this.updateMigrationStatus(migration, 'error', error.message)
            await Swal.fire({
                title: 'Migration Failed / Timed Out',
                html: `<p>${error.message}</p><p class="text-muted">Check GCP logs for the last processed Client ID, then resume from there.</p>`,
                icon: 'error',
                confirmButtonText: 'OK',
            })
            await this.loadMigrationStatuses()
        } finally {
            migration.isRunning = false
        }
    }
}
